import React, { useEffect, useState } from 'react';
import { Input, Select, SelectItem, Button, Chip, Spinner } from '@nextui-org/react';
import { useAuth } from '../../hooks/useAuth';
import { fetchAllUserNotes, NoteWithCourseInfo } from '../../services/notesService';
import { getCachedNotes, setCachedNotes, clearNotesCache } from '../../services/cacheService';
import NoteCard from './NoteCard';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { normalizeText } from '../../utils';

// Helper function to strip HTML and normalize text for search
const normalizeForSearch = (text: string): string => {
    if (!text) return '';

    // First, strip HTML tags and entities
    let cleaned = text
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
        .replace(/&amp;/g, '&') // Replace &amp; with &
        .replace(/&lt;/g, '<') // Replace &lt; with <
        .replace(/&gt;/g, '>') // Replace &gt; with >
        .replace(/&quot;/g, '"') // Replace &quot; with "
        .replace(/&#39;/g, "'") // Replace &#39; with '
        .trim();

    // Apply diacritics normalization first
    let normalized = normalizeText(cleaned);

    // Then handle common Sanskrit-English transliterations
    // Convert both forms to a common normalized form
    const transliterationMap: Record<string, string> = {
        'krishna': 'krishna',
        'krsna': 'krishna',
        'vishnu': 'vishnu',
        'visnu': 'vishnu',
        'shiva': 'shiva',
        'siva': 'shiva'
    };

    // Check if normalized text contains any of these terms and standardize them
    for (const [variant, standard] of Object.entries(transliterationMap)) {
        if (normalized.includes(variant)) {
            normalized = normalized.replace(new RegExp(variant, 'g'), standard);
        }
    }

    return normalized;
};

const MyNotes: React.FC = () => {
    const { user } = useAuth();
    const [notes, setNotes] = useState<NoteWithCourseInfo[]>([]);
    const [filteredNotes, setFilteredNotes] = useState<NoteWithCourseInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'incomplete'>('all');
    const [filterFavorites, setFilterFavorites] = useState<'all' | 'favorites' | 'non-favorites'>('all');
    const [sortBy, setSortBy] = useState<'recent' | 'alphabetical' | 'completion'>('recent');
    const [isUsingCache, setIsUsingCache] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);

    // Rate limiting state with persistence
    const [refreshCooldown, setRefreshCooldown] = useState(0);
    const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);

    const COOLDOWN_STORAGE_KEY = 'myNotesRefreshCooldown';

    // Initialize cooldown state from localStorage on component mount
    useEffect(() => {
        if (user) {
            const savedCooldown = localStorage.getItem(`${COOLDOWN_STORAGE_KEY}_${user.uid}`);
            if (savedCooldown) {
                const { endTime } = JSON.parse(savedCooldown);
                const now = Date.now();
                const remainingTime = Math.max(0, Math.ceil((endTime - now) / 1000));

                if (remainingTime > 0) {
                    setRefreshCooldown(remainingTime);
                    setIsRefreshDisabled(true);
                } else {
                    // Cooldown has expired, clear storage
                    localStorage.removeItem(`${COOLDOWN_STORAGE_KEY}_${user.uid}`);
                }
            }
        }
    }, [user]);

    // Fetch notes on component mount
    useEffect(() => {
        if (user) {
            loadNotesWithCache();
        } else {
            setLoading(false);
        }
    }, [user]);

    // Handle refresh cooldown timer
    useEffect(() => {
        if (refreshCooldown > 0) {
            const timer = setTimeout(() => {
                setRefreshCooldown(prev => {
                    const newValue = Math.max(0, prev - 1);

                    // Update localStorage with new remaining time
                    if (user && newValue > 0) {
                        const endTime = Date.now() + (newValue * 1000);
                        localStorage.setItem(`${COOLDOWN_STORAGE_KEY}_${user.uid}`, JSON.stringify({ endTime }));
                    } else if (user && newValue === 0) {
                        // Cooldown finished, clear storage
                        localStorage.removeItem(`${COOLDOWN_STORAGE_KEY}_${user.uid}`);
                    }

                    return newValue;
                });
            }, 1000);
            return () => clearTimeout(timer);
        } else if (refreshCooldown === 0 && isRefreshDisabled) {
            setIsRefreshDisabled(false);
        }
    }, [refreshCooldown, isRefreshDisabled, user]);

    const loadNotesWithCache = async () => {
        if (!user) return;

        // Try to load from cache first
        const cachedData = getCachedNotes(user.uid);

        if (cachedData) {
            setNotes(cachedData.notes);
            setFilteredNotes(cachedData.notes);
            setIsUsingCache(true);
            setLastFetchTime(new Date(cachedData.timestamp));
            setLoading(false);
        } else {
            // No cache, fetch from Firebase
            await fetchNotesFromFirebase();
        }
    };

    const fetchNotesFromFirebase = async () => {
        if (!user) return;

        setLoading(true);
        setIsUsingCache(false);

        try {
            const userNotes = await fetchAllUserNotes(user.uid);
            setNotes(userNotes);
            setFilteredNotes(userNotes);

            // Cache the results
            setCachedNotes(user.uid, userNotes);
            setLastFetchTime(new Date());
        } catch (error) {
            // Check if it's a permissions error or just no data
            if (error instanceof Error && error.message.includes('permissions')) {
                toast.error('Permission error. Please try logging out and back in.');
            } else {
                toast.error('Failed to load notes');
            }
            console.error('Error fetching notes:', error);
            // Set empty array to show "no notes" message instead of error
            setNotes([]);
            setFilteredNotes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        // Check rate limiting - if we're still in cooldown, don't proceed
        if (refreshCooldown > 0 || isRefreshDisabled) {
            return; // Still in cooldown, don't allow refresh
        }

        // Start cooldown immediately
        setRefreshCooldown(60);
        setIsRefreshDisabled(true);

        // Save cooldown state to localStorage
        if (user) {
            const endTime = Date.now() + (60 * 1000);
            localStorage.setItem(`${COOLDOWN_STORAGE_KEY}_${user.uid}`, JSON.stringify({ endTime }));
        }

        // Clear cache and fetch fresh data
        clearNotesCache();
        await fetchNotesFromFirebase();
        toast.success('Notes refreshed!');
    };

    const getRefreshButtonText = () => {
        if (loading) return 'Refreshing...';
        if (refreshCooldown > 0) return `Wait ${refreshCooldown}s`;
        return 'Refresh';
    };

    // Apply filters and search
    useEffect(() => {
        let filtered = [...notes];

        // Apply search with diacritics normalization
        if (searchTerm) {
            const normalizedSearchTerm = normalizeForSearch(searchTerm);
            filtered = filtered.filter(note =>
                normalizeForSearch(note.notes.content).includes(normalizedSearchTerm) ||
                normalizeForSearch(note.courseName).includes(normalizedSearchTerm) ||
                normalizeForSearch(note.courseDescription).includes(normalizedSearchTerm)
            );
        }

        // Apply status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(note =>
                filterStatus === 'completed' ? note.completionStatus : !note.completionStatus
            );
        }

        // Apply favorites filter
        if (filterFavorites !== 'all') {
            filtered = filtered.filter(note =>
                filterFavorites === 'favorites' ? note.isFavorited : !note.isFavorited
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'recent':
                    const aTime = a.notes.updatedAt?.toMillis ? a.notes.updatedAt.toMillis() : 0;
                    const bTime = b.notes.updatedAt?.toMillis ? b.notes.updatedAt.toMillis() : 0;
                    return bTime - aTime;
                case 'alphabetical':
                    return a.courseName.localeCompare(b.courseName);
                case 'completion':
                    return (b.completionStatus ? 1 : 0) - (a.completionStatus ? 1 : 0);
                default:
                    return 0;
            }
        });

        setFilteredNotes(filtered);
    }, [searchTerm, filterStatus, filterFavorites, sortBy, notes]);

    if (!user) {
        return (
            <div className='container mx-auto px-4 py-8 text-center'>
                <h2 className='text-2xl font-semibold mb-4'>Please log in to view your notes</h2>
                <p className='text-default-500'>Your notes are private and only visible to you.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className='container mx-auto px-4 py-8 flex justify-center items-center min-h-[400px]'>
                <Spinner size='lg' />
            </div>
        );
    }

    return (
        <div className='container mx-auto px-4 py-8'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
                <h1 className='text-3xl font-bold text-darkBlue dark:text-lightGrey'>My Personal Notes</h1>
                <div className='flex items-center gap-2'>
                    {isUsingCache && lastFetchTime && (
                        <Chip color='warning' variant='flat' size='sm'>
                            Cached: {formatDistanceToNow(lastFetchTime)} ago
                        </Chip>
                    )}
                    <Button
                        color='primary'
                        variant='flat'
                        size='sm'
                        onPress={handleRefresh}
                        isLoading={loading}
                        isDisabled={isRefreshDisabled}
                    >
                        <svg className='w-4 h-4 mr-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                        </svg>
                        {getRefreshButtonText()}
                    </Button>
                </div>
            </div>

            {/* Search and Filter Controls */}
            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8'>
                <Input
                    label='Search notes'
                    placeholder='Search notes...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    startContent={
                        <svg className='w-4 h-4 text-default-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                        </svg>
                    }
                    classNames={{
                        inputWrapper: "bg-default-100 border border-default-200",
                    }}
                />

                <Select
                    label='Filter by status'
                    selectedKeys={[filterStatus]}
                    onChange={(e) => setFilterStatus(e.target.value as any)}
                    classNames={{
                        trigger: "bg-default-100 border border-default-200",
                    }}
                >
                    <SelectItem key='all' value='all'>All Notes</SelectItem>
                    <SelectItem key='completed' value='completed'>Completed</SelectItem>
                    <SelectItem key='incomplete' value='incomplete'>In Progress</SelectItem>
                </Select>

                <Select
                    label='Filter by favorites'
                    selectedKeys={[filterFavorites]}
                    onChange={(e) => setFilterFavorites(e.target.value as any)}
                    classNames={{
                        trigger: "bg-default-100 border border-default-200",
                    }}
                >
                    <SelectItem key='all' value='all'>All Notes</SelectItem>
                    <SelectItem key='favorites' value='favorites'>Favorites</SelectItem>
                    <SelectItem key='non-favorites' value='non-favorites'>Non-Favorites</SelectItem>
                </Select>

                <Select
                    label='Sort by'
                    selectedKeys={[sortBy]}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    classNames={{
                        trigger: "bg-default-100 border border-default-200",
                    }}
                >
                    <SelectItem key='recent' value='recent'>Most Recent</SelectItem>
                    <SelectItem key='alphabetical' value='alphabetical'>Alphabetical</SelectItem>
                    <SelectItem key='completion' value='completion'>Completion Status</SelectItem>
                </Select>
            </div>

            {/* Info Banner */}
            <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                <div className='flex items-start gap-3'>
                    <div className='flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center'>
                        <svg className='w-3 h-3 text-white' fill='currentColor' viewBox='0 0 20 20'>
                            <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
                        </svg>
                    </div>
                    <p className='text-sm text-darkBlue'>
                        Changes to favorites, completion status, and notes made in video modals are not reflected here immediately. Please use the refresh button to see the latest updates.
                    </p>
                </div>
            </div>

            {/* Notes Grid */}
            {filteredNotes.length === 0 ? (
                <div className='text-center py-12'>
                    <p className='text-lg text-default-500'>
                        {searchTerm || filterStatus !== 'all' || filterFavorites !== 'all'
                            ? 'No notes found matching your criteria.'
                            : 'You haven\'t created any notes yet.'}
                    </p>
                    {!(searchTerm || filterStatus !== 'all' || filterFavorites !== 'all') && (
                        <p className='text-sm text-default-400 mt-2'>
                            To create notes, go to any course video and click the "Add Notes" button.
                        </p>
                    )}
                    {(searchTerm || filterStatus !== 'all' || filterFavorites !== 'all') && (
                        <Button
                            color='primary'
                            variant='light'
                            className='mt-4'
                            onPress={() => {
                                setSearchTerm('');
                                setFilterStatus('all');
                                setFilterFavorites('all');
                            }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>
            ) : (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                    {filteredNotes.map((note, index) => (
                        <motion.div
                            key={`${note.parentDocPath}-${note.userId}`}
                            className='h-[280px]'
                            initial={{
                                opacity: 0,
                                translateX: index % 2 === 0 ? -50 : 50,
                                translateY: -50,
                            }}
                            animate={{
                                opacity: 1,
                                translateX: 0,
                                translateY: 0
                            }}
                            transition={{
                                duration: 0.3,
                                delay: index * 0.1,
                                ease: "easeOut"
                            }}
                        >
                            <NoteCard
                                note={note}
                                onUpdate={fetchNotesFromFirebase}
                            />
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Summary Stats */}
            {notes.length > 0 && (
                <div className='mt-8 p-4 bg-default-100 rounded-lg'>
                    <div className='flex flex-wrap gap-4 justify-center text-sm'>
                        <span>Total Notes: <strong>{notes.length}</strong></span>
                        <span>•</span>
                        <span>Completed: <strong>{notes.filter(n => n.completionStatus).length}</strong></span>
                        <span>•</span>
                        <span>In Progress: <strong>{notes.filter(n => !n.completionStatus).length}</strong></span>
                        <span>•</span>
                        <span>Favorites: <strong>{notes.filter(n => n.isFavorited).length}</strong></span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyNotes; 